import React, { useId } from 'react'

/**
 * A labelled form control, with an optional hint underneath.
 *
 * The hint sits outside the <label> and is tied to the control with
 * aria-describedby: inside the label it became part of the field's name, so a
 * screen reader announced "Held for (days) 1 = highlight the day you hit it"
 * as what the field was called.
 */
export function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  const hintId = useId()
  const control =
    hint && React.isValidElement<{ 'aria-describedby'?: string }>(children)
      ? React.cloneElement(children, { 'aria-describedby': hintId })
      : children

  return (
    <div className="mb-4">
      <label className="block">
        <span className="block text-xs font-sans text-ink/75 mb-1">{label}</span>
        {control}
      </label>
      {hint && (
        <span id={hintId} className="block text-[11px] text-ink/70 mt-1">
          {hint}
        </span>
      )}
    </div>
  )
}

/**
 * Input styling without a width, for the few places that need to set their own.
 * Appending `w-24` to `inputClass` does nothing — it already carries `w-full`,
 * and which wins is decided by stylesheet order, not class order.
 */
export const inputBase =
  'border border-line rounded-sm px-2 py-1.5 text-sm font-sans bg-white focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent'

export const inputClass = `w-full ${inputBase}`
